import { useEffect, useMemo, useState } from 'react';
import { useDataProvider, useTranslate } from 'react-admin';
import {
  Card,
  CardContent,
  CircularProgress,
  Typography,
} from '@mui/material';
import _ from 'lodash';
import { ClientConfigChangedPreviewTree } from './ClientConfigChangedPreviewTree';
import {
  AppDefinition,
  ClientConfigAttachmentLevel,
  ClientConfigTargetLayers,
  buildChangedPreviewTree,
  buildConfigFromDefinitionLayersWithSources,
  buildGroupAttachmentLevelById,
  buildGroupDefinitions,
  getChangedObject,
  loadClientConfigTargetLayers,
} from '../../lib/clientConfigHelpers';
import {
  useAllClientConfigGroupsQuery,
  useClientConfigAppDefaultConfigV0,
} from '../../lib/clientConfigReactQuery';

const EMPTY_GROUP_IDS = new Set<string>();
const EMPTY_KEY_PATHS = new Set<string>();
const noop = () => {};

type ClientConfigTargetPreviewCardProps = {
  targetType: ClientConfigAttachmentLevel;
  targetId?: string | null;
};

/** Read-only version of the ClientConfig Manager preview: resolved config vs the global default. */
export const ClientConfigTargetPreviewCard = ({ targetType, targetId }: ClientConfigTargetPreviewCardProps) => {

  const dataProvider = useDataProvider();
  const translate = useTranslate();
  const { data: configGroups = [], isLoading: loadingGroups, isError: groupsError } = useAllClientConfigGroupsQuery();
  const { data: defaultConfig, isLoading: loadingDefaults, isError: defaultsError } = useClientConfigAppDefaultConfigV0();
  const [targetLayers, setTargetLayers] = useState<ClientConfigTargetLayers | null>(null);
  const [loadingTarget, setLoadingTarget] = useState(false);
  const [hasError, setHasError] = useState(false);

  useEffect(() => {

    if (!targetId) {

      setTargetLayers(null);
      return () => {};

    }

    let cancelled = false;
    setLoadingTarget(true);
    setHasError(false);
    loadClientConfigTargetLayers(dataProvider, targetType, targetId)
      .then((layers) => {

        if (!cancelled) {

          setTargetLayers(layers);

        }

      })
      .catch((error) => {

        console.error(error);
        if (!cancelled) {

          setTargetLayers(null);
          setHasError(true);

        }

      })
      .finally(() => {

        if (!cancelled) {

          setLoadingTarget(false);

        }

      });

    return () => {

      cancelled = true;

    };

  }, [dataProvider, targetId, targetType]);

  const configGroupById = useMemo(() => _.keyBy(configGroups, '_id'), [configGroups]);

  const preview = useMemo(() => {

    const baselineConfig = defaultConfig?.config;
    if (!targetLayers || !baselineConfig) {

      return null;

    }

    const localDefinitions: AppDefinition[] = [
      ...targetLayers.keyDefinitions,
      ...buildGroupDefinitions(targetLayers.groupIds),
    ];
    const definitionLayers = [
      targetLayers.customerDefinitions,
      ...targetLayers.assetGroupDefinitionLayers,
      localDefinitions,
    ];
    const resolved = buildConfigFromDefinitionLayersWithSources(baselineConfig, definitionLayers, configGroupById);
    const changed = getChangedObject(baselineConfig, resolved.config) || {};

    return {
      roots: buildChangedPreviewTree(changed, resolved.sourcesByPath),
      sourcesByPath: resolved.sourcesByPath,
      overriddenSourcesByPath: resolved.overriddenSourcesByPath,
      groupAttachmentLevelById: buildGroupAttachmentLevelById([
        { level: 'customer', definitions: targetLayers.customerDefinitions },
        ...targetLayers.assetGroupDefinitionLayers.map((definitions) => ({
          level: 'group' as const,
          definitions,
        })),
        { level: targetType, definitions: localDefinitions },
      ], configGroupById),
    };

  }, [configGroupById, defaultConfig, targetLayers, targetType]);

  // Missing id, in-flight load, or layers not in yet — null layers must not render as "no changes".
  const isLoading = !targetId || loadingTarget || loadingGroups || loadingDefaults
    || (targetLayers === null && !hasError);
  const previewError = hasError || groupsError || defaultsError;

  return (
    <Card className="grid-card">
      <CardContent>
        <Typography variant="h6" sx={{ mb: 2 }}>
          {translate('clientConfig.manager.changedKeys')}
        </Typography>
        {isLoading && <CircularProgress size={24}/>}
        {!isLoading && previewError && (
          <Typography variant="body2" color="text.secondary">
            {translate('ra.notification.http_error')}
          </Typography>
        )}
        {!isLoading && !previewError && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            {!preview || preview.roots.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                {translate('clientConfig.groups.preview.noChanges')}
              </Typography>
            ) : (
              <ClientConfigChangedPreviewTree
                roots={preview.roots}
                sourcesByPath={preview.sourcesByPath}
                overriddenSourcesByPath={preview.overriddenSourcesByPath}
                previousSourcesByPath={preview.sourcesByPath}
                removedGroupIds={EMPTY_GROUP_IDS}
                addedGroupIds={EMPTY_GROUP_IDS}
                removedInlineKeyPathSet={EMPTY_KEY_PATHS}
                configGroupById={configGroupById}
                groupAttachmentLevelById={preview.groupAttachmentLevelById}
                showGroupAttachmentLevel
                selectedTargetType={null}
                initialKeyDefinitions={[]}
                onRestoreInlineKey={noop}
                onStageInlineKeyRemoval={noop}
              />
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );

};
